import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import useApiClient from "../hooks/useApiClient";
import useAuth from "../hooks/useAuth";
import { BasicMutationOptions } from "../queryClient";

/**
 * Geoankieta (art. 8i ust. 1 pkt 4 u.p.z.p.) — hooki API.
 * Definicja żyje w procedure.payload.survey, odpowiedzi w survey_responses.
 */

export type SurveyQuestionType = "single" | "multi" | "scale" | "text" | "map_points";

export type SurveyOption = { value: string; label: string };

export type SurveyQuestion = {
  id: string;
  type: SurveyQuestionType;
  label: string;
  help?: string;
  required?: boolean;
  section?: string;
  options?: SurveyOption[];
  allowOther?: boolean;
  maxSelections?: number;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  maxLength?: number;
  minPoints?: number;
  maxPoints?: number;
  pointCommentLabel?: string;
  pointCommentRequired?: boolean;
};

export type SurveyDefinition = {
  enabled: boolean;
  title: string;
  intro?: string;
  outro?: string;
  collectEmail?: boolean;
  rodoNote?: string;
  questions: SurveyQuestion[];
  updatedAt?: string;
};

export type SurveyPoint = { lng: number; lat: number; comment?: string };

export type SurveyAnswerValue =
  | string
  | string[]
  | number
  | { points: SurveyPoint[] }
  | { values?: string[]; value?: string; other?: string };

export type SurveyPublicData = {
  procedure: { id: string; name: string; displayName: string; activeFrom: string; activeTo: string };
  definition: SurveyDefinition;
  isOpen: boolean;
  /** Wołający jest zalogowanym adminem tej gminy — może wypełnić ankietę testowo (także poza terminem). */
  canTest?: boolean;
  responsesCount: number;
};

export type SurveyResponseRecord = {
  id: string;
  procedureId: string;
  answers: Record<string, SurveyAnswerValue>;
  points: Array<SurveyPoint & { questionId: string }>;
  email: string | null;
  createdAt: string;
  /** Odpowiedź testowa administratora — poza statystykami i eksportami. */
  isTest?: boolean;
};

export type SurveyQuestionStats = {
  id: string;
  type: SurveyQuestionType;
  label: string;
  answered: number;
  options?: Array<{ value: string; label: string; count: number; percent: number }>;
  otherTexts?: string[];
  average?: number | null;
  distribution?: Record<string, number>;
  texts?: string[];
  pointsCount?: number;
};

export type SurveyStats = {
  procedureId: string;
  title: string;
  responsesCount: number;
  firstResponseAt: string | null;
  lastResponseAt: string | null;
  pointsCount: number;
  questions: SurveyQuestionStats[];
};

const base = (tenantId: string, procedureId: string) =>
  `/tenant/${tenantId}/procedure/${encodeURIComponent(procedureId)}/survey`;

function isAccessTokenFresh(accessToken: string | undefined, bufferSec = 5): boolean {
  if (!accessToken) return false;
  try {
    const payload = jwtDecode<{ exp?: number }>(accessToken);
    return !!payload?.exp && payload.exp - Math.floor(Date.now() / 1000) > bufferSec;
  } catch {
    return false;
  }
}

/**
 * Czy odwiedzający publiczną stronę ankiety jest zalogowanym adminem TEJ
 * gminy (albo superadminem) ze świeżym tokenem. Tylko wtedy publiczne
 * żądania idą przez `privateApiClient` (nagłówek Authorization → backend
 * zwraca `canTest` i przyjmuje `isTest`). Dla gościa — lub admina ze starą
 * sesją w sessionStorage — zostaje `publicApiClient`, żeby interceptor
 * 401 → refresh → logout nie wyrzucił nikogo z publicznej strony na login.
 */
export const useSurveyAdminContext = (tenantId: string): { isAdminHere: boolean } => {
  const { auth } = useAuth();
  const isAdminHere =
    !!auth.isLoggedIn &&
    (auth.isSuperadmin || (auth.isAdmin && auth.tenantId === tenantId)) &&
    isAccessTokenFresh(auth.accessToken);
  return { isAdminHere };
};

/** Publiczna definicja — 404 gdy ankieta wyłączona (traktujemy jako brak). */
export const usePublicSurveyQuery = (tenantId: string, procedureId: string, options: { enabled?: boolean } = {}) => {
  const { publicApiClient, privateApiClient } = useApiClient();
  const { isAdminHere } = useSurveyAdminContext(tenantId);
  const client = isAdminHere ? privateApiClient : publicApiClient;
  return useQuery<SurveyPublicData | null, AxiosError>({
    queryFn: async () => {
      try {
        return (await client.get(base(tenantId, procedureId))).data;
      } catch (err) {
        if ((err as AxiosError).response?.status === 404) return null;
        throw err;
      }
    },
    queryKey: ["survey", "public", tenantId, procedureId, isAdminHere ? "admin" : "guest"],
    enabled: options.enabled !== false && !!tenantId && !!procedureId,
    staleTime: 60_000,
    retry: false,
  });
};

export const useSubmitSurveyMutation = (
  mutationOptions?: BasicMutationOptions<{ id: string; createdAt: string; isTest?: boolean }, AxiosError>,
) => {
  const { publicApiClient, privateApiClient } = useApiClient();
  const { auth } = useAuth();
  return useMutation<
    { id: string; createdAt: string; isTest?: boolean },
    AxiosError,
    {
      tenantId: string;
      procedureId: string;
      answers: Record<string, SurveyAnswerValue>;
      email?: string;
      consent: boolean;
      /** Tryb testowy admina — backend honoruje tylko z ważnym tokenem admina tej gminy. */
      isTest?: boolean;
    }
  >({
    mutationFn: async ({ tenantId, procedureId, ...body }) => {
      const isAdminHere =
        !!auth.isLoggedIn &&
        (auth.isSuperadmin || (auth.isAdmin && auth.tenantId === tenantId)) &&
        isAccessTokenFresh(auth.accessToken);
      const client = body.isTest && isAdminHere ? privateApiClient : publicApiClient;
      return (await client.post(`${base(tenantId, procedureId)}/response`, body)).data;
    },
    mutationKey: ["survey", "submit"],
    ...mutationOptions,
  });
};

/** Definicja dla admina (także wyłączona). */
export const useSurveyDefinitionQuery = (tenantId: string, procedureId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<{ definition: SurveyDefinition | null }, AxiosError>({
    queryFn: async () => (await privateApiClient.get(`${base(tenantId, procedureId)}/definition`)).data,
    queryKey: ["survey", "definition", tenantId, procedureId],
    enabled: !!tenantId && !!procedureId,
  });
};

export const useSaveSurveyDefinitionMutation = (
  mutationOptions?: BasicMutationOptions<SurveyDefinition, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<SurveyDefinition, AxiosError, { tenantId: string; procedureId: string; definition: SurveyDefinition }>({
    mutationFn: async ({ tenantId, procedureId, definition }) =>
      (await privateApiClient.put(base(tenantId, procedureId), definition)).data,
    mutationKey: ["survey", "save"],
    ...mutationOptions,
  });
};

export const useSurveyTemplateMutation = (
  mutationOptions?: BasicMutationOptions<SurveyDefinition, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<
    SurveyDefinition,
    AxiosError,
    { tenantId: string; procedureId: string; gmina?: string; plan?: string; localities?: string[] }
  >({
    mutationFn: async ({ tenantId, procedureId, gmina, plan, localities }) =>
      (
        await privateApiClient.get(`${base(tenantId, procedureId)}/template`, {
          params: { gmina, plan, localities: localities?.join(",") },
        })
      ).data,
    mutationKey: ["survey", "template"],
    ...mutationOptions,
  });
};

export const useSurveyStatsQuery = (tenantId: string, procedureId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<SurveyStats, AxiosError>({
    queryFn: async () => (await privateApiClient.get(`${base(tenantId, procedureId)}/stats`)).data,
    queryKey: ["survey", "stats", tenantId, procedureId],
    enabled: !!tenantId && !!procedureId,
  });
};

export const useSurveyResponsesQuery = (tenantId: string, procedureId: string) => {
  const { privateApiClient } = useApiClient();
  return useQuery<SurveyResponseRecord[], AxiosError>({
    queryFn: async () => (await privateApiClient.get(`${base(tenantId, procedureId)}/responses`)).data,
    queryKey: ["survey", "responses", tenantId, procedureId],
    enabled: !!tenantId && !!procedureId,
  });
};

export const useDeleteSurveyResponseMutation = (mutationOptions?: BasicMutationOptions<void, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  return useMutation<void, AxiosError, { tenantId: string; procedureId: string; responseId: string }>({
    mutationFn: async ({ tenantId, procedureId, responseId }) => {
      await privateApiClient.delete(`${base(tenantId, procedureId)}/response/${encodeURIComponent(responseId)}`);
    },
    mutationKey: ["survey", "deleteResponse"],
    ...mutationOptions,
  });
};

/** Pobranie eksportu (CSV / GeoJSON) jako Blob — wymaga auth, więc przez privateApiClient. */
export const useSurveyExportMutation = (mutationOptions?: BasicMutationOptions<Blob, AxiosError>) => {
  const { privateApiClient } = useApiClient();
  return useMutation<Blob, AxiosError, { tenantId: string; procedureId: string; format: "csv" | "geojson" }>({
    mutationFn: async ({ tenantId, procedureId, format }) =>
      (await privateApiClient.get(`${base(tenantId, procedureId)}/export.${format}`, { responseType: "blob" })).data,
    mutationKey: ["survey", "export"],
    ...mutationOptions,
  });
};
